import React, { useEffect, useRef, useState } from 'react'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Link as Payment } from '@mui/icons-material'
import { Alert, Box, CircularProgress, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import { apiZodios } from '~api/apiZodios'
import { Avatar, Button, TextInput } from '~components/common'
import { RECAPTCHA_SITE_KEY } from '~config'
import { Case2WithProcess, CaseWithProcess } from '~pages/CasesScreen/components/CaseWithProcess'
import { RootState } from '~redux/rootReducer'
import { loginUser } from '~redux/slices/authSlice'
import {
  ApplyWithoutVerificationEnum,
  ApplyWithoutVerificationEnumType,
  Expression,
  Identity1,
  Process3,
  Value0,
} from '~types'
import { sanitizeText } from '~utils/textUtils'

import EmailVerification from './components/EmailVerification'

const UnverifiedApply: React.FC<{
  verificationMode: ApplyWithoutVerificationEnumType | null
  handleApply: (email?: string, token?: string) => void
}> = ({ handleApply, verificationMode }) => {
  const [email, setEmail] = useState<string>('')
  const validEmail = email.includes('@') && email.includes('.')
  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleReCaptchaVerify = async (): Promise<void> => {
    if (!executeRecaptcha) {
      console.warn('Execute recaptcha not available yet')
      handleApply(email, undefined)
      return
    }

    try {
      const token = await executeRecaptcha('unverified_apply')
      handleApply(email, token)
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error)
      handleApply(email)
    }
  }

  return (
    <>
      <TextInput
        key='email-input'
        fullWidth
        type='email'
        label='Email Address'
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={email !== '' && !email.includes('@') && !email.includes('.')}
        placeholder={
          verificationMode === ApplyWithoutVerificationEnum.Enum.none
            ? 'Optional'
            : 'example@example.com'
        }
        sx={{ mb: 3, maxWidth: '350px' }}
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Button
          variant='contained'
          color='secondary'
          onClick={handleReCaptchaVerify}
          disabled={verificationMode === ApplyWithoutVerificationEnum.Enum.email && !validEmail}
          sx={{ mt: 1, mb: 3 }}
        >
          {'Begin'}
        </Button>
      </Box>
    </>
  )
}

export const ProcessApplyPage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { organizationHandle, processCode } = useParams<{
    organizationHandle: string
    processCode: string
  }>()
  const user = useSelector((state: RootState) => state?.auth.user)
  const emailIdentities = useSelector((state: RootState) => state?.organization.emailIdentities)

  const [identity, setIdentity] = useState<Identity1 | null>(null)
  const [process, setProcess] = useState<Process3 | null>(null)
  const [cases, setCases] = useState<Case2WithProcess[]>([])
  const [showAllCases, setShowAllCases] = useState(false)
  const [expressions, setExpressions] = useState<Expression[]>([])
  const [constantValues, setConstantValues] = useState<Value0[]>([])

  const managed = emailIdentities?.some(
    (identity) => identity.handle === organizationHandle?.replace(/-/g, '_'),
  )

  const [emailEnter, setEmailEnter] = useState(false)
  const [loading, setLoading] = useState(true)

  const descriptionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadProcess()
    loadIdentity()
  }, [organizationHandle, processCode])

  useEffect(() => {
    if (process) loadCases()
  }, [process])

  const loadCases = async (): Promise<void> => {
    if (!user || !process) return
    const casesResponse = await apiZodios.cases_listCases({
      user_id: user.id,
      filter: {
        requester_ids: [user.identity_id],
        process_ids: [process.id],
        statuses: ['Unsubmitted', 'Open'],
      },
      sort: 'by_last_action_desc',
      offset: 0,
      limit: 100,
    })
    setCases(
      casesResponse.data
        .map((c) => ({ ...c, processSummary: null }))
        .sort((a, b) => new Date(b.created_time).getTime() - new Date(a.created_time).getTime()),
    )
    const processSummaryResponse = await apiZodios.cases_listProcessSummaries({
      user_id: user.id,
      case_ids: casesResponse.data.map((c) => c.id),
    })
    setCases(
      casesResponse.data.map((c) => ({
        ...c,
        processSummary: processSummaryResponse.processes.find((s) => s.case_id === c.id) || null,
      })),
    )
  }

  const loadProcess = async (): Promise<void> => {
    try {
      setLoading(true)
      const processResponse = await apiZodios.cases_getProcess({
        process_info_args: {
          process_code: processCode,
          owner_handle: organizationHandle,
        },
      })
      setProcess(processResponse.process)
      const billSteps = processResponse.process.bill_steps || []
      const charges = billSteps.flatMap((step) => step.charges || [])
      const dollars = charges.map((charge) => charge.dollars)
      const expressions = await apiZodios.cases_listExpressions({
        process_id: processResponse.process.id,
        expression_ids: dollars,
      })
      const constants = expressions.data
        .filter((e) => e.constant && e.type === 'number')
        .map((e) => ({ id: e.id, constant: e.constant }))
      const constantValues = await apiZodios.cases_listValues({
        value_ids: constants.map((c) => c.constant).filter((c) => c !== null) as string[],
      })
      setExpressions(expressions.data)
      setConstantValues(constantValues.values)
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadIdentity = async (): Promise<void> => {
    const identityResponse = await apiZodios.core_getIdentity({
      handle: organizationHandle?.replace(/-/g, '_'),
    })
    setIdentity(identityResponse.identity)
  }

  const handleApply = async (email?: string, token?: string): Promise<void> => {
    if (!process) return
    if (!user && !process.apply_without_verification) return

    try {
      if (process.status === 'live') {
        const emailParam = !user && email && email.trim() !== '' ? email.trim() : undefined
        const startProcessResponse = await apiZodios.cases_startProcess({
          user_id: user?.id,
          process_id: process.id,
          email: emailParam,
          token,
        })

        if (!user && startProcessResponse.user_id) {
          const user = await apiZodios.core_getUser({ user_id: startProcessResponse.user_id })
          dispatch(loginUser(user))
        }

        navigate(`/cases/${startProcessResponse.handle}/apply`)
      } else if (process.status === 'draft') {
        if (!user) return
        const testCaseResponse = await apiZodios.cases_createTestCase({
          user_id: user.id,
          name: process.name,
          owner: process.owner.id,
          process_id: process.id,
        })
        const caseData = await apiZodios.cases_getCase({
          user_id: user.id,
          case_id: testCaseResponse.case_id,
        })
        navigate(`/cases/${caseData.case.handle}/apply`)
      }
    } catch (error) {
      console.error('Failed to apply:', error)
    }
  }

  const Container = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[50],
    minHeight: '100vh',
    paddingTop: '50px',
  }))

  const ContentWrapper = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    margin: '0 auto',
    maxWidth: '550px',
    minHeight: '100vh',
    padding: '30px',
    width: '100%',
    boxShadow: theme.shadows[1],
  }))

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='100vh'>
        <CircularProgress />
      </Box>
    )
  }

  const content = (
    <Container>
      <ContentWrapper>
        {identity && (
          <Box mt={2}>
            {identity.image && (
              <Avatar
                src={identity.image}
                alt='Profile'
                size='80px'
                backgroundColor='transparent'
              />
            )}
            <p className='label-large' style={{ marginTop: '12px', marginBottom: '24px' }}>
              {identity.name.toUpperCase()}
            </p>
          </Box>
        )}

        {process && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant='h1'
              sx={{
                mb: 2,
              }}
            >
              {process.name}
            </Typography>

            {process.status === 'archived' ? (
              <Alert severity='error' sx={{ mb: 2 }}>
                This application is archived and no longer live.
              </Alert>
            ) : !process.appliable && !managed ? (
              <Alert severity='info' sx={{ mb: 2 }}>
                This is an internal application. It can only be started by{' '}
                {identity?.name || 'the organization'}.
              </Alert>
            ) : process.status === 'draft' && !managed ? (
              <Alert severity='info' sx={{ mb: 2 }}>
                This application is currently in draft mode. It will be live soon.
              </Alert>
            ) : emailEnter ? (
              <EmailVerification process={process} />
            ) : (
              <Box>
                {cases.length > 0 && (
                  <>
                    <Alert severity='info' sx={{ mb: 2 }}>
                      {cases.length > 1
                        ? 'You already have open cases.'
                        : 'You already have an open case.'}
                    </Alert>
                    <Box sx={{ mt: 2 }}>
                      {(showAllCases ? cases : cases.slice(0, 1)).map((c) => (
                        <CaseWithProcess key={c.id} case={c} />
                      ))}
                      {cases.length > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant='text'
                            onClick={() => setShowAllCases(!showAllCases)}
                            sx={{ margin: '-10px auto 20px' }}
                          >
                            {showAllCases ? 'Show Less' : `Show All ${cases.length} Cases`}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
                {process.status === 'draft' ? (
                  <Button
                    variant='contained'
                    color='warning'
                    onClick={() => handleApply()}
                    startIcon={<Payment />}
                    title='Test case will be deleted when process goes live'
                    sx={{
                      mt: 1,
                      mb: 3,
                      bgcolor: 'orange',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'darkorange' },
                    }}
                  >
                    Begin Test Case
                  </Button>
                ) : (!user || !user.logged_in) &&
                  (process.apply_without_verification === ApplyWithoutVerificationEnum.Enum.email ||
                    process.apply_without_verification ===
                      ApplyWithoutVerificationEnum.Enum.none) ? (
                  <UnverifiedApply
                    handleApply={handleApply}
                    verificationMode={
                      (process.apply_without_verification as ApplyWithoutVerificationEnumType) ||
                      null
                    }
                  />
                ) : (
                  <>
                    <Button
                      color='secondary'
                      variant={cases.length > 0 ? 'outlined' : 'contained'}
                      onClick={
                        user?.logged_in || process.apply_without_verification
                          ? (): Promise<void> => handleApply()
                          : (): void => setEmailEnter(true)
                      }
                      sx={{ mt: 1, mb: 3 }}
                    >
                      {cases.length > 0 ? 'Begin New' : 'Begin'}
                    </Button>
                  </>
                )}
                {process.description && (
                  <Typography
                    ref={descriptionRef}
                    dangerouslySetInnerHTML={{ __html: sanitizeText(process.description) }}
                    sx={{ mb: 2 }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
        {process && (
          <StepsSection
            process={process}
            expressions={expressions}
            constantValues={constantValues}
          />
        )}
      </ContentWrapper>
    </Container>
  )

  return RECAPTCHA_SITE_KEY ? (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>{content}</GoogleReCaptchaProvider>
  ) : (
    content
  )
}

const StepsSection: React.FC<{
  process: Process3
  expressions: Expression[]
  constantValues: Value0[]
}> = ({ process, expressions, constantValues }) => {
  if (!process) return null

  const StepNumber = styled(Box)(({ theme }) => ({
    background: theme.palette.common.white,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.common.black}`,
    color: theme.palette.common.black,
    fontSize: '18px',
    height: '30px',
    width: '30px',
    minWidth: '30px',
    position: 'absolute',
    left: '-16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }))

  const combinedSteps = [
    ...(process.bill_steps?.map((step) => ({
      type: 'BILL' as const,
      data: step,
      order: step.order,
    })) || []),
    ...(process.form_steps?.map((step) => ({
      type: 'FORM' as const,
      data: step,
      order: step.order,
    })) || []),
  ].sort((a, b) => a.order - b.order)

  return (
    <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2.5 }}>
        Online Steps
      </Typography>

      {combinedSteps.map((step, index) => (
        <Box
          key={index}
          sx={{
            position: 'relative',
            ml: 2.5,
            pl: 4,
            pb: 4,
            borderLeft: index !== combinedSteps.length - 1 ? '2px dashed' : 'none',
            borderColor: 'grey.300',
          }}
        >
          <StepNumber>{index + 1}</StepNumber>

          <Box sx={{ pl: 3 }}>
            <Typography variant='h6' sx={{ mb: 1 }}>
              {step.type === 'BILL' ? step.data.name : step.type === 'FORM' ? step.data.name : ''}
            </Typography>

            <Typography
              dangerouslySetInnerHTML={{
                __html:
                  step.type === 'BILL'
                    ? step.data.description
                    : step.type === 'FORM'
                      ? step.data.description
                      : '',
              }}
            />

            {step.type === 'BILL' && step.data.charges && (
              <Box
                sx={{
                  mt: 3,
                  maxWidth: 500,
                  mx: 'auto',
                  borderTop: 1,
                  borderColor: 'grey.300',
                  pt: 2,
                }}
              >
                {step.data.charges.map((charge, chargeIndex) => {
                  const expression = expressions.find((exp) => exp.id === charge.dollars)
                  const constantValue = constantValues.find(
                    (v) => v.number_value?.value === expression?.constant,
                  )
                  return (
                    <Box key={chargeIndex} sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>{charge.name}</Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {constantValue &&
                          constantValue.type === 'number' &&
                          constantValue.number_value
                            ? `$${Number(constantValue.number_value.number).toFixed(2)}`
                            : 'Calculated Amount'}
                        </Typography>
                      </Box>
                      <Typography color='text.secondary' variant='body2' sx={{ mt: 0.5 }}>
                        {charge.description}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default ProcessApplyPage
